import React, { useEffect } from 'react'
import {
  DcButton,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcText,
  PixelFactoryContext,
  SectionContent,
} from 'pixel-base'
import * as XLSX from 'xlsx'
import { IPixelFactory, ITenantInfo, Warehouse } from '../../../../../../decl'
import {
  validateProducts,
  bulkImportPurchases,
  getSuppliers,
} from '../../../../../utils/api-routes-inventory'
import { FileUploadDropzone } from '../details/components/FileUploadDropzone'

interface PurchaseUploadProps {
  onClose: () => void
  onUploadSuccess?: () => void
  warehousesList?: Warehouse[]
}

interface PurchaseUploadRow {
  poNumber: string
  productSku: string
  productName: string
  productId?: number
  purchaseQuantity: number
  receivedCount: number
  billedCount: number
  damagedCount: number
  createdAt: string
  estimatedDate: string
  isValid: boolean
  validationMessage?: string
}

interface Supplier {
  id: number
  supplier_name: string
  [key: string]: any
}

const PurchaseUpload: React.FC<PurchaseUploadProps> = ({
  onClose,
  onUploadSuccess,
  warehousesList = [],
}) => {
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [isUploading, setIsUploading] = React.useState(false)
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [warehouseId, setWarehouseId] = React.useState<string>('')
  const [supplierId, setSupplierId] = React.useState<string>('')
  const [notes, setNotes] = React.useState('')
  const [suppliers, setSuppliers] = React.useState<Supplier[]>([])
  const [validatedPurchases, setValidatedPurchases] = React.useState<
    PurchaseUploadRow[]
  >([])
  const [showValidatedData, setShowValidatedData] = React.useState(false)
  const tenantInfo: ITenantInfo = JSON.parse(
    localStorage.getItem('tenant_info') || '{}',
  )

  useEffect(() => {
    getSuppliers().subscribe((res: any) => {
      const response = res?.response as { data?: Supplier[] } | Supplier[]
      const data = Array.isArray(response) ? response : response?.data ?? []
      setSuppliers(Array.isArray(data) ? data : [])
    })
  }, [])

  const downloadTemplate = () => {
    const templateData = [
      {
        PO_Number: 'PO-2026-001',
        product_sku: 'SKU-123',
        product_name: 'Sample Product',
        purchase_quantity: 100,
        received_count: 95,
        billed_count: 90,
        damaged_count: 5,
        created_at: '2026-01-06 10:00:00',
        estimated_date: '2026-01-15',
      },
    ]
    const ws = XLSX.utils.json_to_sheet(templateData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, 'purchase_upload_template.xlsx')
    showSuccess('Template downloaded successfully')
  }

  const normalizeKey = (key: string): string => {
    return key
      .toLowerCase()
      .replace(/[_\s-]/g, '')
      .trim()
  }

  const extractValue = (
    row: Record<string, string | number | boolean | null | undefined>,
    matcherKeys: string[],
  ) => {
    const matchedKey = Object.keys(row).find((key) =>
      matcherKeys.includes(normalizeKey(key)),
    )
    if (!matchedKey) return ''
    const value = row[matchedKey]
    if (value === undefined || value === null) return ''
    return value.toString().trim()
  }

  const mapRowsToPurchaseData = (
    rows: Record<string, string | number | boolean | null | undefined>[],
  ): PurchaseUploadRow[] => {
    return rows
      .map((row) => {
        const poNumber = extractValue(row, [
          'po_number',
          'ponumber',
          'po number',
          'purchase_order_number',
        ])
        const productSku = extractValue(row, [
          'product_sku',
          'productsku',
          'product sku',
          'sku',
        ])
        const productName = extractValue(row, [
          'product_name',
          'productname',
          'product name',
          'name',
        ])
        const purchaseQuantity = extractValue(row, [
          'purchase_quantity',
          'purchasequantity',
          'purchase quantity',
          'quantity',
        ])
        const receivedCount = extractValue(row, [
          'received_count',
          'receivedcount',
          'received count',
          'received',
        ])
        const billedCount = extractValue(row, [
          'billed_count',
          'billedcount',
          'billed count',
          'billed',
        ])
        const damagedCount = extractValue(row, [
          'damaged_count',
          'damagedcount',
          'damaged count',
          'damaged',
        ])
        const createdAt = extractValue(row, [
          'created_at',
          'createdat',
          'created at',
          'date',
          'created_date',
        ])
        const estimatedDate = extractValue(row, [
          'estimated_date',
          'estimateddate',
          'estimated date',
          'estimated',
          'eta',
        ])
        const purchaseQuantityNum = parseFloat(purchaseQuantity) || 0

        return {
          poNumber,
          productSku,
          productName,
          purchaseQuantity: purchaseQuantityNum,
          receivedCount: parseFloat(receivedCount) || 0,
          billedCount: parseFloat(billedCount) || 0,
          damagedCount: parseFloat(damagedCount) || 0,
          createdAt,
          estimatedDate,
          isValid: true,
        } as PurchaseUploadRow
      })
      .filter(
        (item) => item.poNumber && item.productSku && item.purchaseQuantity > 0,
      )
  }

  const processUploadedFile = (file: File) => {
    setIsUploading(true)
    showPageLoader()

    const reader = new FileReader()
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const buffer = event.target?.result
        if (!buffer) throw new Error('Unable to read file')

        const data = new Uint8Array(buffer as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json<
          Record<string, string | number | boolean | null | undefined>
        >(worksheet, { defval: '' })

        if (jsonData.length === 0) throw new Error('Uploaded sheet is empty.')

        const purchaseData = mapRowsToPurchaseData(jsonData)

        if (purchaseData.length === 0) {
          throw new Error(
            'No valid rows found. Please ensure your file contains PO_Number, product_sku, product_name, and purchase_quantity columns.',
          )
        }

        const productSkus = [
          ...new Set(purchaseData.map((item) => item.productSku)),
        ].filter((sku) => sku && sku.trim() !== '')

        if (productSkus.length === 0) {
          hidePageLoader()
          setIsUploading(false)
          showFailure('No valid product SKUs found in the uploaded file.')
          return
        }

        const storeId = tenantInfo?.store_id || 1

        validateProducts({
          product_skus: productSkus,
          store_id: storeId,
        }).subscribe(
          (res) => {
            hidePageLoader()
            setIsUploading(false)

            const response = res as {
              response?: {
                data?: Array<{
                  product_sku: string
                  status: string
                  message?: string
                  details?: { product_id?: number }
                }>
                _meta?: {
                  total_skus?: number
                  found_count?: number
                  error_count?: number
                }
                error?: string
              }
            }

            if (response?.response?.data === undefined) {
              if (response?.response?.error) {
                showFailure(response.response.error || 'Validation failed')
              } else {
                showFailure('Validation failed. Please check your data.')
              }
              return
            }

            const validationData = response.response.data
            const skuToProductId = new Map<string, number>()
            const skuToError = new Map<string, string>()

            validationData?.forEach((item: any) => {
              const productId =
                item.details?.product_id ?? item.product_id ?? item.id
              if (item.status === 'success' && productId) {
                skuToProductId.set(item.product_sku, Number(productId))
              } else if (item.status === 'error') {
                skuToError.set(
                  item.product_sku,
                  item.message || 'Validation failed',
                )
              }
            })

            const mappedPurchases: PurchaseUploadRow[] = purchaseData.map(
              (item) => {
                const productId = skuToProductId.get(item.productSku)
                const errorMsg = skuToError.get(item.productSku)
                const isValid = !!productId

                return {
                  ...item,
                  productId,
                  isValid,
                  validationMessage: errorMsg,
                }
              },
            )

            setValidatedPurchases(mappedPurchases)
            setShowValidatedData(true)

            const validCount = mappedPurchases.filter((p) => p.isValid).length
            const invalidCount = mappedPurchases.filter(
              (p) => !p.isValid,
            ).length

            if (invalidCount > 0) {
              showSuccess(
                `Validation completed. ${validCount} valid, ${invalidCount} invalid. Remove invalid products to proceed.`,
              )
            } else {
              showSuccess(
                `All ${validCount} products validated successfully. Review and proceed.`,
              )
            }
          },
          (error: { response?: { error?: string }; message?: string }) => {
            hidePageLoader()
            setIsUploading(false)
            showFailure(
              error?.response?.error ||
                error?.message ||
                'Failed to validate purchase data',
            )
          },
        )
      } catch (err) {
        hidePageLoader()
        setIsUploading(false)
        showFailure(
          err instanceof Error ? err.message : 'Unable to process the file',
        )
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) processUploadedFile(files[0])
  }

  const removeInvalidProduct = (index: number) => {
    setValidatedPurchases((prev) => prev.filter((_, i) => i !== index))
  }

  const validPurchases = validatedPurchases.filter((p) => p.isValid)
  const invalidPurchases = validatedPurchases.filter((p) => !p.isValid)
  const hasInvalidProducts = invalidPurchases.length > 0
  const canProceed = validPurchases.length > 0 && !hasInvalidProducts

  const handleProceedWithImport = () => {
    if (!warehouseId || !supplierId) {
      showFailure('Please select warehouse and supplier.')
      return
    }
    if (!canProceed) return

    setIsProcessing(true)
    showPageLoader()

    const payload = {
      supplier_id: Number(supplierId),
      warehouse_id: Number(warehouseId),
      notes: notes || undefined,
      data: validPurchases.map((item) => ({
        po_number: item.poNumber,
        product_id: item.productId!,
        purchase_quantity: item.purchaseQuantity,
        received_count: item.receivedCount,
        billed_count: item.billedCount,
        damaged_count: item.damagedCount,
        created_at: item.createdAt || '',
        expected_at: item.estimatedDate || item.createdAt || '',
      })),
    }

    bulkImportPurchases(payload).subscribe(
      () => {
        hidePageLoader()
        setIsProcessing(false)
        showSuccess('Purchases imported successfully!')
        onUploadSuccess?.()
        onClose()
      },
      (error: { response?: { error?: string }; message?: string }) => {
        hidePageLoader()
        setIsProcessing(false)
        showFailure(
          error?.response?.error ||
            error?.message ||
            'Failed to import purchases',
        )
      },
    )
  }

  const warehouseOptions = warehousesList.map((w: Warehouse) => ({
    label: w.name,
    value: String((w as { wh_id?: number }).wh_id ?? w.id ?? w),
  }))

  const supplierOptions = suppliers.map((s) => ({
    label: s.supplier_name || `Supplier ${s.id}`,
    value: String(s.id),
  }))

  return (
    <SectionContent style={{ padding: '20px' }}>
      <DcFlexBox flexDirection="column" gap="20px">
        {/* Warehouse, Supplier, Notes */}
        <DcFlexBox flexDirection="column" gap="12px">
          <DcDropDown
            isShowLabel={true}
            customLabel="Warehouse"
            label="Warehouse"
            placeholder="Select Warehouse"
            options={warehouseOptions}
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            required
          />
          <DcDropDown
            isShowLabel={true}
            customLabel="Supplier"
            label="Supplier"
            placeholder="Select Supplier"
            options={supplierOptions}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            required
          />
          <DcInput
            name="notes"
            isShowLabel={true}
            customLabel="Notes"
            label="Notes"
            placeholder="Add notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DcFlexBox>

        {!showValidatedData ? (
          <>
            <DcText textSize="14px" style={{ color: '#6c757d' }}>
              Easily upload your purchases with our{' '}
              <span
                style={{
                  color: '#0d6efd',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 500,
                }}
                onClick={() => !isUploading && downloadTemplate()}
              >
                template
              </span>
              . Required columns: PO_Number, product_sku, product_name,
              purchase_quantity, received_count, billed_count, damaged_count,
              created_at, estimated_date.
            </DcText>

            <DcFlexBox flexDirection="column" gap="10px">
              <DcText
                textSize="14px"
                style={{ fontWeight: '600', color: '#495057' }}
              >
                Upload File
              </DcText>
              <FileUploadDropzone onFileSelect={handleFileSelect} />
            </DcFlexBox>
          </>
        ) : (
          <>
            {/* Valid / Invalid Counts */}
            <DcFlexBox gap="16px" flexWrap="wrap">
              <DcFlexBox
                style={{
                  background: '#d4edda',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #28a745',
                }}
              >
                <DcText
                  textSize="14px"
                  style={{ fontWeight: '600', color: '#155724' }}
                >
                  ✓ Valid: {validPurchases.length}
                </DcText>
              </DcFlexBox>
              <DcFlexBox
                style={{
                  background: hasInvalidProducts ? '#f8d7da' : '#e7f3ff',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: hasInvalidProducts
                    ? '1px solid #dc3545'
                    : '1px solid #0d6efd',
                }}
              >
                <DcText
                  textSize="14px"
                  style={{
                    fontWeight: '600',
                    color: hasInvalidProducts ? '#721c24' : '#0d6efd',
                  }}
                >
                  ✗ Invalid: {invalidPurchases.length}
                  {hasInvalidProducts &&
                    ' - Remove invalid products to proceed'}
                </DcText>
              </DcFlexBox>
            </DcFlexBox>

            {/* Products Table */}
            <div
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                }}
              >
                <thead
                  style={{
                    background: '#f8f9fa',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                        width: '40px',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      PO Number
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Product SKU
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Purchase Qty
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Received
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Billed
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Damaged
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Estimated Date
                    </th>
                    {hasInvalidProducts && (
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: '2px solid #dee2e6',
                          fontWeight: '600',
                          color: '#495057',
                          width: '80px',
                        }}
                      >
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {validatedPurchases.map((item, index) => (
                    <tr
                      key={`${item.poNumber}-${item.productSku}-${index}`}
                      style={{
                        background: item.isValid
                          ? index % 2 === 0
                            ? '#ffffff'
                            : '#f8f9fa'
                          : '#fff5f5',
                      }}
                    >
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          color: item.isValid ? '#28a745' : '#dc3545',
                        }}
                      >
                        {item.isValid ? '✓' : '✗'}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          color: '#495057',
                        }}
                      >
                        {item.poNumber}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          color: '#495057',
                        }}
                      >
                        {item.productSku}
                        {!item.isValid && item.validationMessage && (
                          <div
                            style={{
                              fontSize: '10px',
                              color: '#dc3545',
                              marginTop: '2px',
                            }}
                          >
                            {item.validationMessage}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'center',
                          color: '#495057',
                        }}
                      >
                        {item.purchaseQuantity}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'center',
                          color: '#495057',
                        }}
                      >
                        {item.receivedCount}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'center',
                          color: '#495057',
                        }}
                      >
                        {item.billedCount}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'center',
                          color: '#495057',
                        }}
                      >
                        {item.damagedCount}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #dee2e6',
                          color: '#495057',
                        }}
                      >
                        {item.estimatedDate}
                      </td>
                      {hasInvalidProducts && (
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid #dee2e6',
                            textAlign: 'center',
                          }}
                        >
                          {!item.isValid && (
                            <DcButton
                              size="sm"
                              variant="outline-danger"
                              onClick={() => removeInvalidProduct(index)}
                            >
                              Remove
                            </DcButton>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Required Fields Info */}
        <DcFlexBox
          style={{
            background: '#f8f9fa',
            padding: '12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
          }}
          flexDirection="column"
          gap="8px"
        >
          <DcText
            textSize="12px"
            style={{ fontWeight: '600', color: '#495057' }}
          >
            📝 Required Fields:
          </DcText>
          <DcText textSize="11px" style={{ color: '#6c757d' }}>
            PO_Number, product_sku, product_name, purchase_quantity,
            received_count, billed_count, damaged_count, created_at,
            estimated_date
          </DcText>
        </DcFlexBox>

        {/* Action Buttons */}
        <DcFlexBox justifyContent="flex-end" gap="10px" padding="10px 0">
          {showValidatedData ? (
            <>
              <DcButton
                size="sm"
                variant="outline-secondary"
                onClick={() => {
                  setShowValidatedData(false)
                  setValidatedPurchases([])
                }}
                disabled={isProcessing}
              >
                Upload Again
              </DcButton>
              <DcButton
                size="sm"
                variant="primary"
                onClick={handleProceedWithImport}
                disabled={isProcessing || !canProceed}
              >
                {isProcessing ? 'Processing...' : 'Proceed'}
              </DcButton>
            </>
          ) : (
            <DcButton
              size="sm"
              variant="outline-secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </DcButton>
          )}
        </DcFlexBox>
      </DcFlexBox>
    </SectionContent>
  )
}

export default PurchaseUpload
